import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (error) {
      // Still show success to prevent enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-12">
            <Link to="/" className="text-4xl md:text-5xl font-medium tracking-tight leading-tight text-foreground hover:text-accent transition-colors">
              Decided
            </Link>
            <p className="mt-3 text-lg leading-relaxed text-muted-foreground">
              Reset your password
            </p>
          </div>

          {submitted ? (
            <div className="space-y-6">
              <p className="text-muted-foreground leading-relaxed">
                If an account exists for that email, we've sent a password reset link.
              </p>
              <p className="text-sm text-muted-foreground/70">
                Check your inbox (and spam folder) for the reset email.
              </p>
              <Link to="/login">
                <Button 
                  variant="outline" 
                  className="w-full py-6 text-base rounded-sm"
                  data-testid="back-to-login-button"
                >
                  Back to login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="space-y-2 form-group">
                  <Label htmlFor="email" className="text-sm tracking-wide text-muted-foreground uppercase form-label">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-underline text-lg py-3"
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-6 text-base rounded-sm btn-animated"
                  data-testid="forgot-submit-button"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
              </form>

              <p className="mt-8 text-center text-muted-foreground">
                Remember your password?{' '}
                <Link 
                  to="/login" 
                  className="text-foreground hover:text-accent transition-colors underline underline-offset-4"
                  data-testid="login-link"
                >
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 auth-bg" />
    </div>
  );
}
